"use client";

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

interface CurrencyContextType {
  currencyCode: string;
  exchangeRate: number;
  symbol: string;
  setCurrency: (code: string) => void;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currencyCode: "USD",
  exchangeRate: 1,
  symbol: "$",
  setCurrency: () => {},
});

interface CurrencyInfo {
  code: string;
  symbol: string;
  exchangeRate: number;
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyInfo>({
    code: "USD",
    symbol: "$",
    exchangeRate: 1,
  });

  useEffect(() => {
    // Load saved currency preference
    const saved = localStorage.getItem("preferred_currency");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCurrencyState(parsed);
      } catch {}
    }
  }, []);

  const setCurrency = async (code: string) => {
    try {
      const res = await fetch(`/api/currencies?code=${code}`);
      if (res.ok) {
        const data = await res.json();
        if (data.currency) {
          const info = {
            code: data.currency.code,
            symbol: data.currency.symbol,
            exchangeRate: parseFloat(data.currency.exchangeRate),
          };
          setCurrencyState(info);
          localStorage.setItem("preferred_currency", JSON.stringify(info));
        }
      }
    } catch {
      // Fallback to USD
    }
  };

  return (
    <CurrencyContext.Provider
      value={{
        currencyCode: currency.code,
        exchangeRate: currency.exchangeRate,
        symbol: currency.symbol,
        setCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
